/**
 * `useHotelRoom` — subscribes to the hotel room when the component mounts.
 *
 * Mount inside a hotel-scoped route. The hook subscribes on mount and
 * unsubscribes on unmount so navigating away leaves no dangling subscriptions.
 * The reconnect protocol tracks this room and re-subscribes after reconnects.
 */
import { useEffect } from 'react';
import { subscribeHotelRoom } from '../rooms';
import { useRealtimeContext } from './RealtimeProvider';

export function useHotelRoom(tenantId: string, hotelId: string): void {
  const { socket, reconnectManager } = useRealtimeContext();

  useEffect(() => {
    // Track before subscribing so reconnect manager knows about this room.
    reconnectManager.trackHotelRoom(tenantId, hotelId);

    const unsubscribe = subscribeHotelRoom(socket, hotelId);

    return () => {
      reconnectManager.untrackHotelRoom(tenantId, hotelId);
      unsubscribe();
    };
  }, [socket, reconnectManager, tenantId, hotelId]);
}
